import React, { useEffect, useState, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopHeader from './TopHeader';
import QuickAddModal from '@/components/pipeline/QuickAddModal';
import { Plus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

export default function AppShell() {
    const { profile } = useAuth();
    const [sideOpen, setSideOpen] = useState(false);
    const [quickAddOpen, setQuickAddOpen] = useState(false);

    const openQuickAdd = useCallback(() => setQuickAddOpen(true), []);

    // global keyboard shortcut: "N" opens quick add (when not in input)
    useEffect(() => {
        const onKey = (e) => {
            const tag = (e.target?.tagName || '').toLowerCase();
            if (['input', 'textarea', 'select'].includes(tag) || e.target?.isContentEditable) return;
            if (e.key === 'n' || e.key === 'N') {
                e.preventDefault();
                setQuickAddOpen(true);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // Realtime: org-wide live leads toast (admins see all; agents only their own)
    useEffect(() => {
        if (!profile?.organization_id) return;
        const channel = supabase
            .channel(`pc-shell-${profile.organization_id}`)
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'pipeline_contacts', filter: `organization_id=eq.${profile.organization_id}` },
                (payload) => {
                    const c = payload.new;
                    if (profile.role !== 'admin' && c.assigned_to !== profile.id) return;
                    const sourceLabel = c.source === 'Other' && c.source_detail ? `Other - ${c.source_detail}` : (c.source || 'Unknown');
                    toast.success(`New lead from ${sourceLabel}`, { description: `${c.full_name} · ${c.phone || ''}`.trim() });
                }
            )
            .subscribe();
        return () => { supabase.removeChannel(channel); };
    }, [profile?.organization_id, profile?.role, profile?.id]);

    return (
        <div className="min-h-screen flex bg-[#f7f8fa]">
            <Sidebar open={sideOpen} onClose={() => setSideOpen(false)} />
            <div className="flex-1 flex flex-col min-w-0">
                <TopHeader onMenu={() => setSideOpen(true)} onQuickAdd={openQuickAdd} />
                <main className="flex-1 min-w-0">
                    <Outlet context={{ openQuickAdd }} />
                </main>
            </div>
            <button
                onClick={openQuickAdd}
                className="fixed bottom-6 right-6 h-14 w-14 rounded-full bg-aks-gold text-aks-navy shadow-xl shadow-black/20 flex items-center justify-center hover:scale-105 active:scale-95 transition aks-focus-ring z-30"
                aria-label="Quick add lead"
                title="Quick add lead (N)"
                data-testid="fab-quick-add"
            >
                <Plus className="h-6 w-6" />
            </button>
            <QuickAddModal open={quickAddOpen} onClose={() => setQuickAddOpen(false)} />
        </div>
    );
}
